import { flash, FormGroup, Label, Modal, roundNumber, SubmitButton, Tooltip } from "@xola/ui-kit";
import { getLineItemByCode, handleSuccessfulSubmission, isNonCanceledExperienceItem } from "common";
import { isNil } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    ExperiencePurchaseItem,
    LineItem,
    LineItemCode,
    LineItemObject,
    LineItemType,
    Purchase,
    PurchaseItem,
} from "types";
import { api } from "@/api/api";
import { ConfirmLeaveModal } from "@/components/Common/ConfirmLeaveModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { CurrencyInput } from "@/components/Input/CurrencyInput";
import { getValueFromGratuitySelection, GratuitySelector } from "@/components/PurchaseCreate/GratuitySelector";
import { PaymentBreakdown, PriceStrategy } from "@/components/PurchaseModification/PaymentBreakdown";
import { PurchaseItemSelect } from "@/components/PurchaseModification/PurchaseItemSelect";
import { useModal } from "@/hooks/useVisibility";
import { getGratuityEligibleItems } from "@/models/Purchase";
import { GratuitySelection } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface EditGratuityModalProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isOpen: boolean;
    onClose: (purchase?: Purchase) => void;
}

interface AddLineItemOperationProps {
    path: string;
    op: string;
    data: {
        amount: number;
        object: LineItemObject;
        type: LineItemType;
    };
}

interface EditGratuityPayloadProps {
    item?: Partial<PurchaseItem>;
    ops?: AddLineItemOperationProps[];
    _priceStrategy?: PriceStrategy;
}

export const EditGratuityModal = ({ purchase, item, isOpen, onClose }: EditGratuityModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ purchase, item, onClose }: Omit<EditGratuityModalProps, "isOpen">) => {
    const seller = getSeller();
    const gratuityEligibleItems = getGratuityEligibleItems(purchase);
    const [selectedItem, setSelectedItem] = useState<ExperiencePurchaseItem>(item);
    const existingGratuity = useMemo(() => {
        return selectedItem.lineItems.find((lineItem: LineItem) => lineItem.type === LineItemType.GRATUITY);
    }, [selectedItem.lineItems]);
    const [selectedGratuity, setSelectedGratuity] = useState<number>();
    const [dirty, setDirty] = useState(false);

    const subtotalLineItem = getLineItemByCode(selectedItem as PurchaseItem, LineItemCode.UNIT);
    const itemUnitSubtotal = subtotalLineItem?.amount ?? 0;
    const confirmLeaveModal = useModal();
    const [nextItem, setNextItem] = useState<ExperiencePurchaseItem | null>(null);
    const shouldDisableSave =
        isNil(selectedGratuity) ||
        selectedGratuity === existingGratuity?.amount ||
        !isItemGratuityEligible(gratuityEligibleItems, item) ||
        !isNonCanceledExperienceItem(item);
    const tooltipContent = !isNonCanceledExperienceItem(item)
        ? "You cannot add or modify charges on a canceled purchase"
        : "Gratuity is not enabled for this experience";

    useEffect(() => {
        // reset when item changes
        setSelectedGratuity(existingGratuity?.amount);
    }, [existingGratuity?.amount]);

    const handleItemChange = (item: PurchaseItem) => {
        if (dirty) {
            setNextItem(item as ExperiencePurchaseItem);
            confirmLeaveModal.show();
            return;
        }
        setSelectedItem(item as ExperiencePurchaseItem);
    };

    const handleConfirmLeave = () => {
        if (nextItem) {
            setSelectedItem(nextItem);
        }
        setNextItem(null);
        setDirty(false);
        confirmLeaveModal.hide();
    };

    const handleCancelLeave = () => {
        setNextItem(null);
        confirmLeaveModal.hide();
    };

    const handleGratuityChange = (gratuitySelection: GratuitySelection) => {
        const gratuityValue = getValueFromGratuitySelection(gratuitySelection, itemUnitSubtotal);
        setSelectedGratuity(gratuityValue);
        setDirty(true);
    };

    const updatedPurchase = useMemo(() => {
        if (existingGratuity) {
            // modify gratuity
            return {
                items: [
                    {
                        id: selectedItem.id,
                        lineItems: [
                            {
                                id: existingGratuity.id,
                                amount: selectedGratuity,
                            },
                        ],
                        // Gratuity is unrelated to item pricing, so this must never trigger any demographic/line item price recalculation.
                        _priceStrategy: PriceStrategy.RETAIN,
                    },
                ],
            };
        } else {
            // add gratuity
            return {
                ops: [
                    {
                        path: `/items/${selectedItem.id}/lineItems`,
                        op: "addLineItem",
                        data: {
                            object: LineItemObject.DEFAULT,
                            type: LineItemType.GRATUITY,
                            amount: roundNumber(purchase.currency, selectedGratuity),
                        },
                    },
                ],
                _priceStrategy: PriceStrategy.RETAIN,
            };
        }
    }, [selectedGratuity, existingGratuity, purchase.currency, selectedItem.id]);

    const {
        mutateAsync: applyChangeGratuity,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: EditGratuityPayloadProps) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
    });

    const handleSubmit = async () => {
        try {
            const { data: purchase } = await applyChangeGratuity(updatedPurchase);
            handleSuccessfulSubmission(() => {
                updateCache("purchases", purchase);
                onClose(purchase);
            });
        } catch {
            flash.danger("Error saving gratuity");
        }
    };

    return (
        <>
            <Modal.Header>{existingGratuity ? "Modify Gratuity" : "Add Gratuity"}</Modal.Header>
            <Modal.Body className="flex-col space-y-4">
                <ConfirmLeaveModal
                    disableRouterPrompt
                    copy="You have unsaved changes on this screen. Navigating away will cause you to lose these changes. Are you sure you want to continue?"
                    manualShowPrompt={confirmLeaveModal.isVisible}
                    handleConfirm={handleConfirmLeave}
                    handleCancel={handleCancelLeave}
                />
                {gratuityEligibleItems.length > 1 ? (
                    <PurchaseItemSelect
                        items={gratuityEligibleItems}
                        value={selectedItem}
                        className="flex-1"
                        onChange={handleItemChange}
                    />
                ) : null}
                <div className="flex space-x-4">
                    <div className="flex-1">
                        {existingGratuity || seller.preferences.gratuity.options.length === 0 ? (
                            <FormGroup>
                                <Label>Gratuity</Label>
                                <CurrencyInput
                                    value={selectedGratuity}
                                    isAllowed={(values) => {
                                        const { floatValue } = values;
                                        return (floatValue ?? 0) >= 0; // only allow positive values
                                    }}
                                    onValueChange={({ value }) => {
                                        setDirty(true);
                                        const numericValue = Number.parseFloat(value);
                                        if (Number.isNaN(numericValue)) {
                                            setSelectedGratuity(undefined);
                                        } else {
                                            setSelectedGratuity(numericValue);
                                        }
                                    }}
                                />
                            </FormGroup>
                        ) : (
                            <GratuitySelector
                                item={selectedItem}
                                itemUnitSubtotal={itemUnitSubtotal}
                                onGratuityChange={(amount) => handleGratuityChange(amount)}
                            />
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="bg-gray-lighter p-6">
                            <PaymentBreakdown
                                isEnabled={!isNil(selectedGratuity)}
                                isEditable={false}
                                purchase={{ ...purchase }}
                                item={selectedItem}
                                payload={updatedPurchase}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <Tooltip
                    content={tooltipContent}
                    disabled={isNonCanceledExperienceItem(item) && isItemGratuityEligible(gratuityEligibleItems, item)}
                >
                    <SubmitButton
                        disabled={shouldDisableSave}
                        isLoading={isLoading}
                        isSuccess={isSuccess}
                        onClick={handleSubmit}
                    >
                        Save
                    </SubmitButton>
                </Tooltip>
            </Modal.Footer>
        </>
    );
};

const isItemGratuityEligible = (eligibleItems: ExperiencePurchaseItem[], item: PurchaseItem) => {
    return eligibleItems.some((eligibleItem) => eligibleItem.id === item.id);
};
